import {HeaderTitle, Notification} from '@/components/atoms';
import PropTypes from 'prop-types';
import {PageHeader} from '@/components/molecules';
import {RewardCatalogList} from '@/containers/RewardCatalog/RewardCatalogList';
import {Header} from '@/utils/constants/header';
import {getNotificationConfig} from '@/utils/constants/rewardCatalogConstants';
import {DIGITAL_CODES_NOTIFICATIONS} from '@/utils/constants/commonConstants';
import NotificationAlert from '@/components/atoms/Toast/NotificationAlert';
import {useState, useEffect, useCallback} from 'react';
import {useAppSelector, useAppDispatch} from '@/store';
import {
  selectOpenUpload,
  selectUploadErrorMessage,
  selectUploading,
  selectSuccessfullyUploaded,
  openUploadModal
} from '@/store/slices/rewardBulkUpload';
import useLowInventory from '@/hooks/useLowInventory';
import './rewardCatalogHome.scss';

const RewardCatalogHome = ({instanceKey}) => {
  const dispatch = useAppDispatch();

  // Get upload states from Redux store
  const openUpload = useAppSelector(selectOpenUpload);
  const uploadErrorMessage = useAppSelector(selectUploadErrorMessage);
  const uploading = useAppSelector(selectUploading);
  const successfullyUploaded = useAppSelector(selectSuccessfullyUploaded);

  // Handle opening modal through Redux action
  const handleOpenModal = () => {
    dispatch(openUploadModal());
  };

  const [notificationState, setNotificationState] = useState(null);

  // Update notification state based on upload conditions
  useEffect(() => {
    if (!openUpload && uploadErrorMessage) {
      setNotificationState('error');
    } else if (!openUpload && successfullyUploaded) {
      setNotificationState('success');
    } else if (!openUpload && uploading) {
      setNotificationState('info');
    } else {
      setNotificationState(null);
    }
  }, [openUpload, uploadErrorMessage, successfullyUploaded, uploading]);

  // Digital codes low inventory
  const {zeroLeftRewards, lowInventoryRewards} = useLowInventory();

  const [rewardIdFilter, setRewardIdFilter] = useState([]);

  const applyRewardIdFilter = useCallback((rewards) => {
    const ids = (rewards || [])
      .map((r) => r?.rewardId ?? r?.id)
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id));
    setRewardIdFilter(ids);
  }, []);

  const handleZeroLeftClick = useCallback(
    () => applyRewardIdFilter(zeroLeftRewards),
    [applyRewardIdFilter, zeroLeftRewards]
  );

  const handleLowInventoryClick = useCallback(
    () => applyRewardIdFilter(lowInventoryRewards),
    [applyRewardIdFilter, lowInventoryRewards]
  );

  const notificationConfig = getNotificationConfig(notificationState);

  return (
    <div key={instanceKey} className='rewardCatalogContainer flex flex-col'>
      <PageHeader title='Reward Catalog' />
      <NotificationAlert
        type={notificationConfig?.type}
        message={notificationConfig?.message}
        show={notificationConfig?.show}
        arrowButton={notificationConfig?.arrowButton}
        iconType={notificationConfig?.iconType}
        buttonShow={notificationConfig?.buttonShow}
        handleArrow={handleOpenModal}
      />
      <div className='grid grid-cols-3 gap-4'>
        {zeroLeftRewards?.length > 0 && (
          <div className='col-span-3 md:col-span-1'>
            <Notification
              className='rewards-digital-codes-notification info'
              primaryActionIcon={
                DIGITAL_CODES_NOTIFICATIONS.PRIMARY_ACTION_ICON
              }
              primaryLabel=''
              primaryActionOnClick={handleZeroLeftClick}
              showActionButtons
              showIcon
              showText
              showTitle
              text={
                zeroLeftRewards?.length === 1
                  ? DIGITAL_CODES_NOTIFICATIONS.ZERO_LEFT.TEXT
                  : `${DIGITAL_CODES_NOTIFICATIONS.ZERO_LEFT.TEXT}\n${DIGITAL_CODES_NOTIFICATIONS.TOTAL_REWARDS_TEXT}: ${zeroLeftRewards?.length}`
              }
              title={DIGITAL_CODES_NOTIFICATIONS.ZERO_LEFT.TITLE}
              type='info'
            />
          </div>
        )}
        {lowInventoryRewards?.length > 0 && (
          <div className='col-span-3 md:col-span-1'>
            <Notification
              className='rewards-digital-codes-notification warning'
              primaryLabel=''
              primaryActionIcon={
                DIGITAL_CODES_NOTIFICATIONS.PRIMARY_ACTION_ICON
              }
              primaryActionOnClick={handleLowInventoryClick}
              showActionButtons
              showIcon
              showText
              showTitle
              text={
                lowInventoryRewards?.length === 1
                  ? DIGITAL_CODES_NOTIFICATIONS.LOW_INVENTORY.TEXT
                  : `${DIGITAL_CODES_NOTIFICATIONS.LOW_INVENTORY.TEXT}\n${DIGITAL_CODES_NOTIFICATIONS.TOTAL_REWARDS_TEXT}: ${lowInventoryRewards?.length}`
              }
              title={DIGITAL_CODES_NOTIFICATIONS.LOW_INVENTORY.TITLE}
              type='warning'
            />
          </div>
        )}
      </div>
      <div
        style={{backgroundColor: '#1D1D1D', paddingLeft: '16px'}}
        className='rewardCatalogTitleSection relative'>
        <HeaderTitle
          headingLevel={Header.SectionTitle}
          title='Reward Catalog List'
          className='rewardCatalogDataTableHeading'
        />
        <RewardCatalogList rewardIdFilter={rewardIdFilter} />
      </div>
    </div>
  );
};

RewardCatalogHome.propTypes = {
  instanceKey: PropTypes.string.isRequired
};

export default RewardCatalogHome;
